import { Button } from '@/components/ui/button'
import { cn, formatMoney, formatPercent } from '@/lib/utils'
import { smartSellBasisLabels } from '../pricing/pricing-rules'
import { AiData } from '../ui/ai-data'
import { ManufacturerLink, ProductLink } from './links'
import { ProductImage } from './product-image'
import { leadTimeLabel, productCategoryContext } from './product-text'
import type { ProductSummary } from './queries'

const amount = (value: string | null | undefined) => (value == null ? null : Number(value))

/** What the product costs us, what the market charges and where a quote line starts (USA-1140). */
function CatalogPrices({ price, label }: { price: ProductSummary['price']; label: string }) {
  const net = amount(price.net_amount)
  const quoteAt = amount(price.suggested_sell_amount)
  const margin = quoteAt && net !== null && quoteAt > 0 ? ((quoteAt - net) / quoteAt) * 100 : null
  const facts = [
    { label: 'Net', value: net },
    { label: 'MAP', value: amount(price.map_amount) },
    { label: 'Online', value: amount(price.lowest_online_amount) },
    { label: 'Store', value: amount(price.sell_amount) },
  ]
  if (quoteAt === null && facts.every((fact) => fact.value === null)) return null
  return (
    <div aria-label={`Prices for ${label}`} className="min-w-0 tabular-nums" role="group">
      {quoteAt !== null ? (
        <p
          className="flex flex-wrap items-baseline justify-start gap-x-2 sm:justify-end"
          title={price.suggested_sell_basis ? smartSellBasisLabels[price.suggested_sell_basis] : undefined}
        >
          <span className="text-xs text-muted-foreground">Quote at</span>
          <span className="text-base font-semibold">{formatMoney(quoteAt)}</span>
          {margin !== null ? <span className="text-xs text-muted-foreground">{formatPercent(margin)} margin</span> : null}
        </p>
      ) : null}
      <dl className="mt-1 grid grid-cols-4 gap-x-3 text-xs sm:grid-cols-[repeat(4,auto)] sm:justify-end">
        {facts.map((fact) => (
          <div className="min-w-0 sm:text-right" key={fact.label}>
            <dt className="text-muted-foreground">{fact.label}</dt>
            <dd className="font-medium">{fact.value === null ? '-' : formatMoney(fact.value)}</dd>
          </div>
        ))}
      </dl>
    </div>
  )
}

/** One catalog search result (demo `CatalogResultCard`): identity, category, lead time, prices and the action. */
export function CatalogResultCard({
  product,
  busy = false,
  onSelect,
  action = 'Project',
  note,
}: {
  product: ProductSummary
  busy?: boolean
  onSelect: (product: ProductSummary) => void
  action?: 'Project' | 'Replace'
  // Where we buy a quote-only product, or that it is priced on each quote.
  note?: string | null
}) {
  const identity = `${product.manufacturer} ${product.model}`
  const category = productCategoryContext(product)
  const showDescription = product.title.trim().toLocaleLowerCase() !== identity.toLocaleLowerCase()
  const descriptionId = `catalog-product-${product.id}`
  const leadTime = leadTimeLabel(product)
  const selectionLabel = [
    `${action}: ${identity}`,
    showDescription ? product.title : null,
    category.category,
    category.productType ? `Product type ${category.productType}` : null,
    leadTime,
  ]
    .filter(Boolean)
    .join('. ')

  return (
    <article className="grid grid-cols-[56px_minmax(0,1fr)] gap-3 rounded-lg border bg-card p-3 sm:grid-cols-[64px_minmax(0,1fr)_auto]">
      <ProductImage alt="" className="size-14 rounded-md border sm:size-16" url={product.primary_asset?.url} />
      <div className="min-w-0 self-start sm:self-center">
        <p className="flex flex-wrap items-baseline gap-x-1.5 text-sm font-semibold leading-5">
          <ManufacturerLink uuid={product.manufacturer_uuid}>{product.manufacturer}</ManufacturerLink>
          <ProductLink uuid={product.uuid}>{product.model}</ProductLink>
        </p>
        {showDescription ? (
          <p className="mt-0.5 line-clamp-3 text-xs leading-4 text-muted-foreground" id={descriptionId}>
            {product.title}
          </p>
        ) : null}
        <p className="mt-1.5 grid gap-0.5 text-xs leading-4 text-muted-foreground">
          <span className="font-medium text-foreground/80">{category.category}</span>
          {category.productType ? <span>{category.productType}</span> : null}
          {note ? <span>{note}</span> : null}
        </p>
        <AiData
          className="mt-2 inline-block py-1 pl-2 pr-6 text-xs text-muted-foreground"
          label="AI-assisted lead-time estimate"
        >
          {leadTime}
        </AiData>
      </div>
      <div className="col-span-2 sm:col-span-1 sm:self-center">
        <CatalogPrices label={identity} price={product.price} />
      </div>
      <div className={cn('col-span-2 flex items-center justify-end gap-2 sm:col-span-3')}>
        {action === 'Project' ? (
          <Button
            disabled
            size="sm"
            title="Adding an accessory from search is not available yet"
            type="button"
            variant="outline"
          >
            Accessory
          </Button>
        ) : null}
        <Button
          aria-describedby={showDescription ? descriptionId : undefined}
          aria-label={selectionLabel}
          disabled={busy}
          onClick={() => onSelect(product)}
          size="sm"
          title={showDescription ? product.title : identity}
          type="button"
        >
          {action}
        </Button>
      </div>
    </article>
  )
}
